import { Link } from 'react-router-dom';

import { DocPage } from '@/components/doc-page';
import { DynamicText } from '@/components/dynamic-text';

export default function TermsPage() {
  return (
    <DocPage
      kicker={<DynamicText path="terms.kicker">Terms of service</DynamicText>}
      title={<DynamicText path="terms.title">Terms of Service</DynamicText>}
      lead={
        <DynamicText path="terms.lead">
          These terms govern your use of Bramble and our website.
        </DynamicText>
      }
      updated={<DynamicText path="terms.updated">14 September 2026</DynamicText>}
    >
      <h2>
        <DynamicText path="terms.s1.heading">1. Acceptance</DynamicText>
      </h2>
      <p>
        By downloading or using Bramble (the &ldquo;app&rdquo;) or this website, you agree to these
        terms. If you do not agree, do not use them. You terminate this agreement by deleting your
        account, which you can request from the <Link to="/support">support page</Link>.
      </p>

      <h2>
        <DynamicText path="terms.s2.heading">2. Bramble is not medical advice</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s2.p1">
          Bramble is a personal record-keeping tool. It is not a medical device, and nothing in it
          is medical advice, diagnosis, or treatment. Growth logs, charts, feeding totals, sleep
          summaries, and anything you record about illnesses or medicines are calculated from, or
          simply repeat back, what you entered. They are not evaluated against WHO or CDC growth
          curves, are not reviewed by a clinician, and carry no clinical meaning on their own.
        </DynamicText>
      </p>
      <ul>
        <li>
          <DynamicText path="terms.s2.item1">
            Always consult a pediatrician or qualified health professional about your child’s
            feeding, weight, growth, sleep, or health.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s2.item2">
            Dosage schedules and dose reminders repeat back the interval you entered. They are not a
            dosing recommendation, are not checked against a prescription or your child’s weight,
            and can be missed or delayed by the operating system. Follow the instructions on the
            medicine and from your pediatrician, and do not rely on Bramble to tell you what or when
            to give.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s2.item3">
            Never delay seeking medical care, or disregard advice you have been given, because of
            information in this app.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s2.item4">
            If you believe your child has a medical emergency, call your local emergency number
            immediately. Do not use the app or the support form.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s2.item5">
            Do not rely on Bramble as the sole record for a clinical decision. Data can be lost with a
            lost phone, and sync can be delayed (see section 4).
          </DynamicText>
        </li>
      </ul>

      <h2>
        <DynamicText path="terms.s3.heading">3. Acceptable use</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s3.intro">You agree not to:</DynamicText>
      </p>
      <ul>
        <li>
          <DynamicText path="terms.s3.item1">
            Use Bramble or its API for anything other than logging and syncing your own
            family’s child-care records.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item2">
            Use the service as general-purpose file storage, a message bus, or a transport for
            unrelated data.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item3">
            Attempt to overload, flood, disrupt, or gain unauthorized access to the service, this
            website, or another user’s account or records.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item4">
            Share account credentials with anyone you would not invite as a caregiver, or attempt to
            reach a family you were not invited to.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item5">
            Reverse engineer the service to circumvent rate limits or automate support form
            submissions.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item6">
            Use Bramble to record data about a child without legal right or parental authority.
          </DynamicText>
        </li>
        <li>
          <DynamicText path="terms.s3.item7">
            Use the service in violation of applicable laws.
          </DynamicText>
        </li>
      </ul>
      <p>
        <DynamicText path="terms.s3.closing">
          We reserve the right to suspend accounts or block IP addresses violating these terms.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="terms.s4.heading">4. Availability, sync, and data</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s4.p1">
          The app keeps a copy of your records on your phone and logs without a connection, but the
          service itself is provided without uptime guarantees. Sync is not instant or guaranteed: a
          change reaches another caregiver’s device when that device next connects, and wake
          notifications are best-effort.
        </DynamicText>
      </p>
      <p>
        <strong>You are responsible for your own backups.</strong> We keep operational backups to
        run the service, but they exist for our recovery, not yours &mdash; they are not a personal
        backup, and we do not undertake to restore data you delete. Deletion is permanent. See the{' '}
        <Link to="/privacy">privacy policy</Link>.
      </p>
      <p>
        <DynamicText path="terms.s4.p3">
          We may modify, suspend, or discontinue any part of the service at any time. We will provide
          notice in app release notes when practical.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="terms.s5.heading">5. Your content</DynamicText>
      </h2>
      <p>
        You retain all rights to your data. We claim no license over it beyond what is needed to
        store it, sync it between the caregivers you invite, and show it back to you. We do not
        sell it, share it with advertisers, or use it to train anything. You are responsible for the
        content you record. Note that our servers can technically read what you store &mdash; the{' '}
        <Link to="/privacy">privacy policy</Link> is explicit about this.
      </p>

      <h2>
        <DynamicText path="terms.s6.heading">6. Bramble Sharing subscriptions</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s6.p1">
          All on-device tracking features are free, as is creating a Bramble account and syncing
          your own log between your own devices. Co-parent sync, account-based caregiver access,
          and timed babysitting links require an active auto-renewing Bramble Sharing subscription.
          One subscription covers up to four registered caregivers in total, including the purchaser,
          across the families they belong to.
        </DynamicText>
      </p>
      <p>
        <DynamicText path="terms.s6.p2">
          Bramble Sharing is offered monthly. In the United States its price is $4.99 per month; the
          localized price shown by the App Store before purchase is the price that applies to you.
          Payment is charged to your Apple ID when you confirm the purchase. The subscription renews
          automatically unless canceled at least 24 hours before the end of the current period, and
          Apple may charge for renewal within 24 hours before that period ends. You can manage or
          cancel it in your Apple ID subscription settings. Deleting Bramble or your Bramble account
          does not cancel the Apple subscription.
        </DynamicText>
      </p>
      <p>
        Apple processes purchases and refunds under its terms. If Sharing expires or is revoked,
        remote sync and caregiver access pause; on-device tracking and the records already held on
        a device remain available. These terms supplement Apple&rsquo;s{' '}
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          rel="noreferrer noopener"
          target="_blank"
        >
          Standard End User License Agreement
        </a>
        .
      </p>

      <h2>
        <DynamicText path="terms.s7.heading">7. Disclaimer of warranties</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s7.p1">
          Bramble is provided “as is” and “as available”, without warranties of any kind, express
          or implied. We do not warrant that the app will be uninterrupted or error-free, or that
          data will sync, arrive in order, or be preserved.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="terms.s8.heading">8. Limitation of liability</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s8.p1">
          To the fullest extent permitted by law, Bramble and its creators are not liable for any
          indirect, incidental, special, consequential, or punitive damages, or loss of data or
          health outcomes, arising from your use of the app or the service, even if advised of the
          possibility.
        </DynamicText>
      </p>
      <p>
        <DynamicText path="terms.s8.p2">
          Total aggregate liability for all claims relating to the service is limited to the amount
          you paid for Bramble Sharing in the 12 months before the claim or USD 50, whichever is
          greater.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="terms.s9.heading">9. Changes to these terms</DynamicText>
      </h2>
      <p>
        <DynamicText path="terms.s9.p1">
          We may update these terms. Updated dates will appear at the top of this page, and material
          changes will be noted in release notes. Continued use of Bramble constitutes acceptance of
          the updated terms.
        </DynamicText>
      </p>

      <h2>
        <DynamicText path="terms.s10.heading">10. Contact</DynamicText>
      </h2>
      <p>
        Questions regarding these terms can be sent via the <Link to="/support">support page</Link>.
      </p>
    </DocPage>
  );
}
